import {useCallback, useEffect, useState} from "react";
import {Button, Empty, Input, Modal, Spin, Typography, message} from "antd";
import {CopyOutlined, UploadOutlined} from "@ant-design/icons";
import {useNavigate, useParams} from "react-router-dom";
import PageTitle from "../../components/title/pageTitle";
import Navigation from "../../components/navigation/navigation";
import Footer from "../../components/footer/footer";
import RetouchCompare from "../../components/retouchReview/RetouchCompare";
import RetouchSingleUploadModal from "../../components/retouchReview/RetouchSingleUploadModal";
import {fetchRetouchReviewInfo} from "../../utils/retouchReviewApi";
import {buildRetouchReviewUrl, getRetouchName, setRetouchName} from "../../utils/retouchReviewToken";
import {getRetouchReviewCopy} from "../../utils/retouchReviewCopy";
import copyToClipboard from "../../utils/copy";
import {useTranslation} from "../../i18n";
import "./retouchReview.css";

function isWechatDesktopBrowser() {
    if (typeof navigator === "undefined") return false;
    const ua = navigator.userAgent || "";
    const isWechat = /MicroMessenger|WindowsWechat|MacWechat/i.test(ua);
    const isMobile = /Android|iPhone|iPad|iPod|Mobile|Windows Phone/i.test(ua)
        || (navigator.maxTouchPoints > 1 && /Macintosh/i.test(ua));
    return isWechat && !isMobile;
}

function RetouchReview() {
    const {token} = useParams();
    const navigate = useNavigate();
    const {locale} = useTranslation();
    const copy = getRetouchReviewCopy(locale);
    const [review, setReview] = useState(null);
    const [status, setStatus] = useState("loading");
    const [name, setName] = useState(() => getRetouchName(token));
    const [nameSubmitted, setNameSubmitted] = useState(() => Boolean(getRetouchName(token).trim()));
    const [selected, setSelected] = useState(null);
    const [uploadPhotoTarget, setUploadPhotoTarget] = useState(null);
    const [wechatPromptOpen, setWechatPromptOpen] = useState(false);
    const [wechatPromptDismissed, setWechatPromptDismissed] = useState(false);
    const load = useCallback(async () => {
        setStatus("loading");
        try {
            const nextReview = await fetchRetouchReviewInfo(token);
            setReview(nextReview);
            setSelected((previous) => nextReview?.photos?.find((photo) => photo.id === previous?.id) || nextReview?.photos?.[0] || null);
            setStatus("ready");
        } catch (error) {
            setStatus(error.status === 404 ? "invalid" : "error");
        }
    }, [token]);
    useEffect(() => {
        load();
    }, [load]);
    useEffect(() => {
        if (status === "ready" && review?.photos?.length && !selected) {
            setSelected(review.photos[0]);
        }
    }, [review, selected, status]);
    useEffect(() => {
        if (status === "ready" && review && isWechatDesktopBrowser() && !wechatPromptDismissed) {
            setWechatPromptOpen(true);
        }
    }, [review, status, wechatPromptDismissed]);
    const updatePhoto = (next) => {
        setReview((prev) => ({...prev, photos: prev.photos.map((photo) => photo.id === next.id ? next : photo)}));
        setSelected((prev) => prev?.id === next.id ? next : prev);
    };
    const navigatePhoto = (next) => setSelected(next);
    const submitName = () => {
        const trimmed = name.trim();
        if (!trimmed) return;
        setRetouchName(token, trimmed);
        setName(trimmed);
        setNameSubmitted(true);
    };
    const dismissWechatPrompt = () => {
        setWechatPromptOpen(false);
        setWechatPromptDismissed(true);
    };
    const copyReviewUrl = async () => {
        try {
            await copyToClipboard(buildRetouchReviewUrl(token));
            message.success(copy.wechatBrowserCopied);
        } catch {
            message.error(copy.wechatBrowserCopyFailed);
        }
    };
    const uploadPhoto = (photo) => setUploadPhotoTarget(photo);
    const uploadBatch = () => navigate(`/r/${token}/upload/new`);
    if (status === "loading") return <><PageTitle><Navigation/></PageTitle>
        <div className="retouch-review-loading"><Spin/></div>
        <Footer/></>;
    if (status !== "ready" || !review) return <><PageTitle><Navigation/></PageTitle>
        <main className="retouch-review-page">
            <section className="retouch-review-panel"><Typography.Title
                level={3}>{copy.expired}</Typography.Title><Button onClick={load}>{copy.rescan}</Button></section>
        </main>
        <Footer/></>;
    const isRetoucher = review.role === "retoucher";
    const displayName = isRetoucher ? copy.retoucher : name;
    const wechatBrowserPrompt = <Modal open={wechatPromptOpen} title={copy.wechatBrowserTitle}
                                       onCancel={dismissWechatPrompt} footer={[<Button key="close"
                                                                                       onClick={dismissWechatPrompt}>{copy.wechatBrowserClose}</Button>,
        <Button key="copy" type="primary" icon={<CopyOutlined/>}
                onClick={copyReviewUrl}>{copy.wechatBrowserCopyLink}</Button>]}><Typography.Paragraph>{copy.wechatBrowserMessage}</Typography.Paragraph><Typography.Paragraph
        type="secondary">{copy.wechatBrowserOpenHint}</Typography.Paragraph><Input readOnly
                                                                                   value={buildRetouchReviewUrl(token)}
                                                                                   onFocus={(event) => event.target.select()}/></Modal>;
    if (!isRetoucher && !nameSubmitted) return <><PageTitle><Navigation/></PageTitle>
        <main className="retouch-review-page">
            <section className="retouch-review-panel retouch-review-name-gate"><Typography.Title level={3}
                                                                                                 className="retouch-review-name-gate__title">{copy.enterNameTitle}</Typography.Title><Typography.Paragraph
                className="retouch-review-name-gate__album">{review.albumName}</Typography.Paragraph><Typography.Paragraph
                className="retouch-review-name-gate__subtitle">{copy.enterName}</Typography.Paragraph><Input
                size="large" maxLength={80} value={name} placeholder={copy.enterNamePlaceholder}
                onChange={(event) => setName(event.target.value)} onPressEnter={(event) => {
                if (!event.nativeEvent?.isComposing && event.keyCode !== 229) submitName();
            }}/><Button type="primary" size="large" block style={{marginTop: 12}} disabled={!name.trim()}
                        onClick={submitName}>{copy.startReview}</Button></section>
        </main>
        {wechatBrowserPrompt}<Footer/></>;
    return <><PageTitle><Navigation/></PageTitle>
        <main className="retouch-review-page">
            <header className="retouch-review-page__header"><h1>{review.albumName}</h1>
                <p>{isRetoucher ? copy.retoucherTitle : copy.customerTitle}</p>
                <div className="retouch-review-actions"
                     style={{justifyContent: "center"}}>{review.capabilities?.canUpload &&
                    <Button icon={<UploadOutlined/>} onClick={uploadBatch}>{copy.upload}</Button>}</div>
            </header>
            {review.photos.length === 0 && <Empty description={copy.noFiles}/>}{selected &&
            <RetouchCompare photo={selected} photos={review.photos} token={token} role={review.role}
                            authorName={displayName} onPhotoChanged={updatePhoto} onNavigate={navigatePhoto}
                            onUploadPhoto={uploadPhoto} onUploadBatch={uploadBatch}/>}</main>
        <RetouchSingleUploadModal open={Boolean(uploadPhotoTarget)} photo={uploadPhotoTarget} reviewId={review.id}
                                  token={token} copy={copy} onClose={() => setUploadPhotoTarget(null)}
                                  onUploaded={load}/>{wechatBrowserPrompt}<Footer/></>;
}

export default RetouchReview;
